import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { useCallback, useRef, useState } from 'react';
import type { NodeViewProps } from '@tiptap/react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    resizableImage: {
      setImage: (options: { src: string; alt?: string; title?: string; width?: string }) => ReturnType;
    };
  }
}

const MIN_WIDTH = 60;
const MAX_WIDTH_PERCENT = 100;

/**
 * Past de afbeelding niet meer op zijn vel, dan verkleint of verschuift de paginering
 * van het hele document (sheetPagination) hem: in de editor net als voor de klant.
 */
function ResizableImageView({ node, updateAttributes, selected }: NodeViewProps) {
  const { src, alt, title, width } = node.attrs;
  const containerRef = useRef<HTMLDivElement>(null);
  const [resizing, setResizing] = useState(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, direction: 'left' | 'right') => {
      e.preventDefault();
      e.stopPropagation();
      setResizing(true);

      const startX = e.clientX;
      const imgEl = containerRef.current?.querySelector('img');
      if (!imgEl) return;
      const startWidth = imgEl.getBoundingClientRect().width;
      const parentWidth = containerRef.current?.parentElement?.getBoundingClientRect().width || startWidth;

      const onMouseMove = (ev: MouseEvent) => {
        const delta = direction === 'right' ? ev.clientX - startX : startX - ev.clientX;
        const newPx = Math.max(MIN_WIDTH, startWidth + delta);
        const newPercent = Math.min(MAX_WIDTH_PERCENT, Math.round((newPx / parentWidth) * 100));
        updateAttributes({ width: `${newPercent}%` });
      };

      const onMouseUp = () => {
        setResizing(false);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [updateAttributes],
  );

  return (
    <NodeViewWrapper
      as="div"
      className="resizable-image-wrapper"
      style={{ position: 'relative' }}
    >
      <div style={{ display: 'flex' }}>
        <div
          ref={containerRef}
          style={{
            width: width || 'auto',
            maxWidth: '100%',
            position: 'relative',
            display: 'inline-block',
          }}
          data-drag-handle
        >
          <img
            src={src}
            alt={alt || ''}
            title={title || undefined}
            draggable={false}
            style={{
              width: '100%',
              display: 'block',
              borderRadius: 4,
              outline: selected ? '2px solid rgb(0, 7, 236)' : 'none',
              outlineOffset: 2,
            }}
          />
          {selected && (
            <>
              <div
                onMouseDown={(e) => handleMouseDown(e, 'left')}
                style={{
                  position: 'absolute',
                  left: -4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 8,
                  height: 32,
                  borderRadius: 4,
                  backgroundColor: 'rgb(0, 7, 236)',
                  cursor: resizing ? 'col-resize' : 'ew-resize',
                  zIndex: 10,
                }}
              />
              <div
                onMouseDown={(e) => handleMouseDown(e, 'right')}
                style={{
                  position: 'absolute',
                  right: -4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 8,
                  height: 32,
                  borderRadius: 4,
                  backgroundColor: 'rgb(0, 7, 236)',
                  cursor: resizing ? 'col-resize' : 'ew-resize',
                  zIndex: 10,
                }}
              />
            </>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
}

export const ResizableImage = Node.create({
  name: 'image',
  group: 'block',
  draggable: true,
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      // renderHTML schrijft de breedte als inline style; zonder parseHTML ging hij bij herladen verloren.
      width: {
        default: null,
        parseHTML: (element) => element.style.width || element.getAttribute('width') || null,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[src]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const { width, ...rest } = HTMLAttributes;
    const style = width ? `width: ${width}` : undefined;
    return ['img', mergeAttributes(rest, ...(style ? [{ style }] : []))];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView) as any;
  },

  addCommands() {
    return {
      setImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});
